import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Container,
  IconButton,
  Tooltip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  Translate as TranslateIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { contactPageService, globalService } from '../../services/firebase';
import { ContactPageData, FAQItem, ContactFormData, TranslatableField } from '../../types';
import { Timestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { getTranslatedData, setTranslatedData } from '../../utils/getTranslatedData';

// Reusable Dialog for editing FAQ items
const FAQDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (item: FAQItem) => void;
  item: FAQItem | null;
  selectedLang: string;
}> = ({ open, onClose, onSave, item, selectedLang }) => {
  const [editedItem, setEditedItem] = useState<FAQItem | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (item) {
      const question = typeof item.question === 'string' ? { tr: item.question, en: '', ar: '' } : item.question;
      const answer = typeof item.answer === 'string' ? { tr: item.answer, en: '', ar: '' } : item.answer;
      setEditedItem({ ...item, question, answer });
    }
  }, [item]);

  const handleFieldChange = (field: 'question' | 'answer', value: string) => {
    if (!editedItem) return;
    const updated = setTranslatedData(editedItem, field, selectedLang, value);
    setEditedItem(updated);
  };

  const handleSimpleFieldChange = (field: keyof FAQItem, value: any) => {
    if (editedItem) {
      setEditedItem({ ...editedItem, [field]: value });
    }
  };
  
  const handleTranslate = async () => {
    if (!editedItem) return;
    setIsTranslating(true);
    try {
        const questionTr = getTranslatedData(editedItem.question, 'tr');
        const answerTr = getTranslatedData(editedItem.answer, 'tr');

        const [questionTranslations, answerTranslations] = await Promise.all([
            globalService.translateTexts({ text: questionTr, sourceLang: 'tr', targetLangs: ['en', 'ar'] }),
            globalService.translateTexts({ text: answerTr, sourceLang: 'tr', targetLangs: ['en', 'ar'] })
        ]);

        let updatedItem = setTranslatedData(editedItem, 'question', 'en', (questionTranslations as any).en);
        updatedItem = setTranslatedData(updatedItem, 'question', 'ar', (questionTranslations as any).ar);
        updatedItem = setTranslatedData(updatedItem, 'answer', 'en', (answerTranslations as any).en);
        updatedItem = setTranslatedData(updatedItem, 'answer', 'ar', (answerTranslations as any).ar);

        setEditedItem(updatedItem);
    } catch (error) {
        console.error("Translation failed:", error);
    } finally {
        setIsTranslating(false);
    }
  };

  const handleSave = () => {
    if (editedItem) {
      onSave(editedItem);
    }
    onClose();
  };

  if (!editedItem) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{item?.id.startsWith('new-') ? 'Yeni SSS Ekle' : 'SSS Düzenle'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Soru"
            fullWidth
            value={getTranslatedData(editedItem.question, selectedLang)}
            onChange={(e) => handleFieldChange('question', e.target.value)}
          />
          <TextField
            label="Cevap"
            multiline
            rows={5}
            fullWidth
            value={getTranslatedData(editedItem.answer, selectedLang)}
            onChange={(e) => handleFieldChange('answer', e.target.value)}
          />
          <TextField
            label="Sıra"
            type="number"
            value={editedItem.order}
            onChange={(e) => handleSimpleFieldChange('order', Number(e.target.value))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editedItem.isActive}
                onChange={(e) => handleSimpleFieldChange('isActive', e.target.checked)}
              />
            }
            label="Aktif"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Tooltip title="Tümünü Çevir (TR'den)">
          <span>
            <IconButton onClick={handleTranslate} disabled={isTranslating || selectedLang !== 'tr'}>
              {isTranslating ? <CircularProgress size={24} /> : <TranslateIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{flexGrow: 1}}/>
        <Button onClick={onClose}>İptal</Button>
        <Button onClick={handleSave} variant="contained">Kaydet</Button>
      </DialogActions>
    </Dialog>
  );
};

const ManageContactPage: React.FC = () => {
  const [pageData, setPageData] = useState<ContactPageData | null>(null);
  const [messages, setMessages] = useState<(ContactFormData & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState('tr');
  const { t } = useTranslation();
  
  const [faqDialog, setFaqDialog] = useState<{ open: boolean; item: FAQItem | null }>({ open: false, item: null });

  const normalizeField = useCallback((field: any): TranslatableField => {
    if (typeof field === 'string') {
      return { tr: field, en: '', ar: '' };
    }
    return field || { tr: '', en: '', ar: '' };
  }, []);

  const normalizeData = useCallback((data: ContactPageData): ContactPageData => {
    
    data.heroSection = {
        title: normalizeField(data.heroSection?.title),
        description: normalizeField(data.heroSection?.description)
    };
    data.contactForm = {
        title: normalizeField(data.contactForm?.title)
    };
    data.contactInfo = {
        address: {
            street: normalizeField(data.contactInfo?.address?.street),
            district: normalizeField(data.contactInfo?.address?.district),
            city: normalizeField(data.contactInfo?.address?.city),
            country: normalizeField(data.contactInfo?.address?.country),
        },
        phones: data.contactInfo?.phones || [],
        emails: data.contactInfo?.emails || [],
        officeHours: {
            weekdays: normalizeField(data.contactInfo?.officeHours?.weekdays),
            saturday: normalizeField(data.contactInfo?.officeHours?.saturday),
            sunday: normalizeField(data.contactInfo?.officeHours?.sunday),
        }
    };
    
    if (data.faq && data.faq.items) {
      data.faq.items = data.faq.items.map(item => ({
        ...item,
        question: normalizeField(item.question),
        answer: normalizeField(item.answer),
      }));
    } else {
        data.faq = { items: [] };
    }
    return data;
  }, [normalizeField]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let data = await contactPageService.getContactPageData();
      if (!data) {
        data = await contactPageService.initializeContactPageData();
      }
      const contactMessages = await contactPageService.getContactMessages();
      
      const normalized = normalizeData(data);
      setPageData(normalized);
      setMessages(contactMessages as (ContactFormData & { id: string })[]);
    } catch (err: any) {
      setError(`Veri yüklenirken hata: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [normalizeData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    if (!pageData) return;
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const dataToSave = { ...pageData, updatedAt: Timestamp.now() };
      await contactPageService.updateContactPageData(dataToSave);
      setPageData(dataToSave);
      setSuccess('Sayfa başarıyla güncellendi!');
    } catch (err: any) {
      setError(`Kaydederken hata oluştu: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTranslateAll = async () => {
    if (!pageData) return;
    setIsSaving(true);
    setError(null);
    
    try {
      let dataCopy = JSON.parse(JSON.stringify(pageData));

      const fieldsToTranslate = [
        'heroSection.title', 'heroSection.description',
        'contactForm.title',
        'contactInfo.address.street', 'contactInfo.address.district', 'contactInfo.address.city', 'contactInfo.address.country',
        'contactInfo.officeHours.weekdays', 'contactInfo.officeHours.saturday', 'contactInfo.officeHours.sunday'
      ];

      for (const path of fieldsToTranslate) {
          const keys = path.split('.');
          let parentObj: any = dataCopy;
          
          // Traverse to the parent of the target field
          for (let i = 0; i < keys.length - 1; i++) {
            if (parentObj[keys[i]] === undefined || parentObj[keys[i]] === null) {
              parentObj = undefined; // Path does not exist, so we can't translate.
              break;
            }
            parentObj = parentObj[keys[i]];
          }

          if (parentObj) {
            const fieldName = keys[keys.length - 1];
            const targetField = parentObj[fieldName];
            const sourceText = getTranslatedData(targetField, 'tr');

            if (sourceText) {
                const translations = await globalService.translateTexts({ text: sourceText, sourceLang: 'tr', targetLangs: ['en', 'ar'] });
                parentObj[fieldName] = { ...normalizeField(targetField), ...translations, tr: sourceText };
            }
          }
      }

      setPageData(dataCopy);
      setSuccess('Tüm alanlar başarıyla çevrildi!');
    } catch (err) {
        setError('Çeviri sırasında bir hata oluştu.');
        console.error(err);
    } finally {
        setIsSaving(false);
    }
  };
  
  const handleSimpleChange = (path: string, value: any) => {
    if (!pageData) return;
    const keys = path.split('.');
    const updatedData = { ...pageData };
    let current: any = updatedData;
    for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = current[keys[i]] || {};
        current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setPageData(updatedData);
  };
  
  const handleTranslatableChange = (path: string, value: string) => {
    if (!pageData) return;
    const keys = path.split('.');
    let updatedData = JSON.parse(JSON.stringify(pageData)); // Deep copy to be safe
    let current: any = updatedData;
  
    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = current[keys[i]] || {};
      current = current[keys[i]];
    }
  
    const fieldName = keys[keys.length - 1];
    
    if (typeof current[fieldName] !== 'object' || current[fieldName] === null) {
        current[fieldName] = { tr: '', en: '', ar: '' };
    }
  
    current[fieldName][selectedLang] = value;
    setPageData(updatedData);
  };

  const handleListChange = (listName: 'phones' | 'emails', index: number, value: string) => {
    if (!pageData?.contactInfo) return;
    const newList = [...(pageData.contactInfo[listName] || [])];
    newList[index] = value;
    handleSimpleChange(`contactInfo.${listName}`, newList);
  };

  const addToList = (listName: 'phones' | 'emails') => {
    if (!pageData?.contactInfo) return;
    const newList = [...(pageData.contactInfo[listName] || []), ''];
    handleSimpleChange(`contactInfo.${listName}`, newList);
  };

  const removeFromList = (listName: 'phones' | 'emails', index: number) => {
    if (!pageData?.contactInfo) return;
    const newList = (pageData.contactInfo[listName] || []).filter((_, i) => i !== index);
    handleSimpleChange(`contactInfo.${listName}`, newList);
  };

  const handleSaveFaq = (item: FAQItem) => {
    if (!pageData) return;
    const items = pageData.faq?.items || [];
    const index = items.findIndex(i => i.id === item.id);
    let newItems;
    if (index > -1) {
      newItems = items.map(i => i.id === item.id ? item : i);
    } else {
      newItems = [...items, item];
    }
    handleSimpleChange('faq.items', newItems);
  };
  
  const handleDeleteFaq = (id: string) => {
    if (!pageData?.faq?.items) return;
    const newItems = pageData.faq.items.filter(i => i.id !== id);
    handleSimpleChange('faq.items', newItems);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (!pageData) return <Alert severity="error">İletişim sayfası verileri yüklenemedi.</Alert>;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">İletişim Sayfası Yönetimi</Typography>
        <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={handleTranslateAll} disabled={isSaving} startIcon={isSaving ? <CircularProgress size={20} /> : <TranslateIcon />}>
                Tümünü Çevir
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={isSaving} startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}>
                Değişiklikleri Kaydet
            </Button>
        </Stack>
      </Stack>

      <ToggleButtonGroup value={selectedLang} exclusive onChange={(_, lang) => lang && setSelectedLang(lang)} size="small" sx={{mb: 2}}>
          <ToggleButton value="tr">TR</ToggleButton>
          <ToggleButton value="en">EN</ToggleButton>
          <ToggleButton value="ar">AR</ToggleButton>
      </ToggleButtonGroup>
      
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', lg: '66.666%' } }}>
          <Stack spacing={3}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Ana Başlık Bölümü</Typography>
              <TextField fullWidth label="Sayfa Başlığı" value={getTranslatedData(pageData.heroSection?.title, selectedLang)} onChange={e => handleTranslatableChange('heroSection.title', e.target.value)} sx={{ mb: 2 }} />
              <TextField fullWidth multiline rows={3} label="Sayfa Açıklaması" value={getTranslatedData(pageData.heroSection?.description, selectedLang)} onChange={e => handleTranslatableChange('heroSection.description', e.target.value)} />
            </Paper>

            <Paper elevation={2} sx={{p:3}}>
                <Typography variant="h6" gutterBottom>İletişim Formu Başlığı</Typography>
                 <TextField fullWidth label="Form Başlığı" value={getTranslatedData(pageData.contactForm?.title, selectedLang)} onChange={e => handleTranslatableChange('contactForm.title', e.target.value)} />
            </Paper>
            
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>İletişim Bilgileri</Typography>
              <Typography variant="subtitle1" gutterBottom>Adres</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField fullWidth label="Sokak" value={getTranslatedData(pageData.contactInfo?.address?.street, selectedLang)} onChange={e => handleTranslatableChange('contactInfo.address.street', e.target.value)} />
                <TextField fullWidth label="İlçe" value={getTranslatedData(pageData.contactInfo?.address?.district, selectedLang)} onChange={e => handleTranslatableChange('contactInfo.address.district', e.target.value)} />
                <TextField fullWidth label="Şehir" value={getTranslatedData(pageData.contactInfo?.address?.city, selectedLang)} onChange={e => handleTranslatableChange('contactInfo.address.city', e.target.value)} />
                <TextField fullWidth label="Ülke" value={getTranslatedData(pageData.contactInfo?.address?.country, selectedLang)} onChange={e => handleTranslatableChange('contactInfo.address.country', e.target.value)} />
              </Box>
              
              <Box sx={{my: 2}}>
                <Typography variant="subtitle1" gutterBottom>Telefon Numaraları</Typography>
                {pageData.contactInfo?.phones?.map((phone, index) => (
                  <Stack direction="row" spacing={1} key={index} sx={{mb: 1}}>
                    <TextField fullWidth size="small" value={phone} onChange={e => handleListChange('phones', index, e.target.value)} />
                    <IconButton onClick={() => removeFromList('phones', index)}><DeleteIcon /></IconButton>
                  </Stack>
                ))}
                <Button onClick={() => addToList('phones')} startIcon={<AddIcon />}>Telefon Ekle</Button>
              </Box>

              <Box sx={{my: 2}}>
                <Typography variant="subtitle1" gutterBottom>E-posta Adresleri</Typography>
                {pageData.contactInfo?.emails?.map((email, index) => (
                  <Stack direction="row" spacing={1} key={index} sx={{mb: 1}}>
                    <TextField fullWidth size="small" value={email} onChange={e => handleListChange('emails', index, e.target.value)} />
                    <IconButton onClick={() => removeFromList('emails', index)}><DeleteIcon /></IconButton>
                  </Stack>
                ))}
                <Button onClick={() => addToList('emails')} startIcon={<AddIcon />}>E-posta Ekle</Button>
              </Box>

              <Typography variant="subtitle1" sx={{mt: 2, mb: 1}}>Çalışma Saatleri</Typography>
              <TextField fullWidth label="Hafta İçi" value={getTranslatedData(pageData.contactInfo?.officeHours?.weekdays, selectedLang)} onChange={e => handleTranslatableChange('contactInfo.officeHours.weekdays', e.target.value)} sx={{mb: 2}}/>
              <TextField fullWidth label="Cumartesi" value={getTranslatedData(pageData.contactInfo?.officeHours?.saturday, selectedLang)} onChange={e => handleTranslatableChange('contactInfo.officeHours.saturday', e.target.value)} sx={{mb: 2}}/>
              <TextField fullWidth label="Pazar" value={getTranslatedData(pageData.contactInfo?.officeHours?.sunday, selectedLang)} onChange={e => handleTranslatableChange('contactInfo.officeHours.sunday', e.target.value)} />
            </Paper>

            <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Harita Bilgileri</Typography>
                <TextField fullWidth label="Google Maps API Key" value={pageData.mapLocation?.googleMapsApiKey || ''} onChange={e => handleSimpleChange('mapLocation.googleMapsApiKey', e.target.value)} sx={{mb: 2}}/>
                <TextField fullWidth label="Enlem (Latitude)" type="number" value={pageData.mapLocation?.latitude || ''} onChange={e => handleSimpleChange('mapLocation.latitude', Number(e.target.value))} sx={{mb: 2}}/>
                <TextField fullWidth label="Boylam (Longitude)" type="number" value={pageData.mapLocation?.longitude || ''} onChange={e => handleSimpleChange('mapLocation.longitude', Number(e.target.value))} />
            </Paper>

            <Paper elevation={2} sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Sıkça Sorulan Sorular</Typography>
                <Button startIcon={<AddIcon />} onClick={() => setFaqDialog({ open: true, item: { id: `new-${Date.now()}`, question: { tr: '', en: '', ar: '' }, answer: { tr: '', en: '', ar: '' }, isActive: true, order: pageData.faq?.items?.length || 0 } })}>SSS Ekle</Button>
              </Stack>
              <List>
                {pageData.faq?.items?.sort((a,b) => a.order - b.order).map((item) => (
                  <ListItem key={item.id} secondaryAction={
                    <>
                      <IconButton edge="end" onClick={() => setFaqDialog({ open: true, item })}><EditIcon /></IconButton>
                      <IconButton edge="end" onClick={() => handleDeleteFaq(item.id)}><DeleteIcon /></IconButton>
                    </>
                  }>
                    <ListItemText primary={getTranslatedData(item.question, selectedLang)} secondary={`Sıra: ${item.order}`} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', lg: '33.333%' } }}>
          <Stack spacing={3}>
             <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Sosyal Medya</Typography>
                <TextField fullWidth label="Whatsapp" value={pageData.socialMedia?.whatsapp || ''} onChange={e => handleSimpleChange('socialMedia.whatsapp', e.target.value)} sx={{ mb: 2 }} />
                <TextField fullWidth label="Facebook" value={pageData.socialMedia?.facebook || ''} onChange={e => handleSimpleChange('socialMedia.facebook', e.target.value)} sx={{ mb: 2 }} />
                <TextField fullWidth label="Instagram" value={pageData.socialMedia?.instagram || ''} onChange={e => handleSimpleChange('socialMedia.instagram', e.target.value)} sx={{ mb: 2 }} />
                <TextField fullWidth label="Twitter" value={pageData.socialMedia?.twitter || ''} onChange={e => handleSimpleChange('socialMedia.twitter', e.target.value)} sx={{ mb: 2 }} />
                <TextField fullWidth label="LinkedIn" value={pageData.socialMedia?.linkedin || ''} onChange={e => handleSimpleChange('socialMedia.linkedin', e.target.value)} sx={{ mb: 2 }} />
                <TextField fullWidth label="Youtube" value={pageData.socialMedia?.youtube || ''} onChange={e => handleSimpleChange('socialMedia.youtube', e.target.value)} />
            </Paper>

            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Bölüm Görünürlük Ayarları</Typography>
              {pageData.sections && Object.keys(pageData.sections).map(key => (
                 <FormControlLabel 
                    key={key}
                    control={<Switch checked={pageData.sections[key as keyof typeof pageData.sections]?.isActive ?? true} onChange={e => handleSimpleChange(`sections.${key}.isActive`, e.target.checked)} />} 
                    label={t(`contact_us.sections.${key}`)}
                />
              ))}
            </Paper>
          </Stack>
        </Box>
      </Box>
      
      <FAQDialog 
        open={faqDialog.open}
        onClose={() => setFaqDialog({ open: false, item: null })}
        onSave={handleSaveFaq}
        item={faqDialog.item}
        selectedLang={selectedLang}
      />
    </Container>
  );
};

export default ManageContactPage; 